import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useAuth } from "@/lib/auth";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Pagination } from "@/components/pagination";
import Navbar from "@/components/navbar";
import {
  ImageOff,
  AlertTriangle,
  Save,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  Image as ImageIcon,
} from "lucide-react";
import type { PaginatedResult } from "@shared/schema";

type Venue = {
  id: number;
  name: string;
  vibeVenueUuid: string | null;
  imageUrl: string | null;
  url: string | null;
  capacity: number | null;
  streetAddress: string | null;
  city: string;
  state: string;
  zipCode: string | null;
  market: string | null;
  country: string;
  lat: number | string | null;
  lng: number | string | null;
  googlePlaceId: string | null;
  createdAt: Date;
};

type ImageStats = { total: number; withImage: number; withoutImage: number };

// --- Thumbnail component ---
function VenueImageThumbnail({
  venue,
  isSelected,
  isBroken,
  onSelect,
  onBroken,
}: {
  venue: Venue;
  isSelected: boolean;
  isBroken: boolean;
  onSelect: () => void;
  onBroken: () => void;
}) {
  const hasImage = !!venue.imageUrl;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`
        relative aspect-square rounded-lg overflow-hidden border-2 transition-all
        focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2
        ${isSelected ? "ring-2 ring-primary border-primary" : ""}
        ${!hasImage ? "bg-neutral-100 border-neutral-200" : ""}
        ${isBroken && hasImage ? "border-red-400" : ""}
        ${hasImage && !isBroken && !isSelected ? "border-neutral-200 hover:border-neutral-400" : ""}
      `}
      title={`${venue.name} — ${venue.city}, ${venue.state}`}
    >
      {hasImage && !isBroken ? (
        <img
          src={venue.imageUrl!}
          alt={venue.name}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={onBroken}
        />
      ) : hasImage && isBroken ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-400">
          <AlertTriangle className="w-6 h-6 mb-1" />
          <span className="text-[10px] leading-tight text-center px-1 truncate max-w-full">Broken</span>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-neutral-400">
          <ImageOff className="w-6 h-6 mb-1" />
          <span className="text-[10px] leading-tight text-center px-1 truncate max-w-full">No image</span>
        </div>
      )}

      {/* Name overlay */}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-1.5 pb-1 pt-4">
        <p className="text-[10px] leading-tight text-white truncate">{venue.name}</p>
      </div>
    </button>
  );
}

// --- Side panel (inline) ---
function EditSidePanel({
  venue,
  editingUrl,
  setEditingUrl,
  previewUrl,
  previewError,
  setPreviewError,
  onSave,
  onClear,
  onClose,
  onPrev,
  onNext,
  isSaving,
  hasPrev,
  hasNext,
}: {
  venue: Venue;
  editingUrl: string;
  setEditingUrl: (url: string) => void;
  previewUrl: string;
  previewError: boolean;
  setPreviewError: (v: boolean) => void;
  onSave: () => void;
  onClear: () => void;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  isSaving: boolean;
  hasPrev: boolean;
  hasNext: boolean;
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h3 className="font-semibold text-lg truncate">{venue.name}</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Current image preview */}
        <div>
          <p className="text-xs text-neutral-500 mb-1">Current image</p>
          <div className="aspect-video rounded-lg overflow-hidden bg-neutral-100 border">
            {venue.imageUrl ? (
              <img
                src={venue.imageUrl}
                alt={venue.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-neutral-400">
                <ImageOff className="w-8 h-8" />
              </div>
            )}
          </div>
        </div>

        {/* Venue details */}
        <div className="text-sm text-neutral-600 space-y-1">
          <p>{venue.city}, {venue.state}</p>
          {venue.capacity != null && <p>Capacity: {venue.capacity.toLocaleString()}</p>}
        </div>

        {/* URL input */}
        <div>
          <label htmlFor="image-url-input" className="text-xs font-medium text-neutral-700 mb-1 block">
            Image URL
          </label>
          <Input
            id="image-url-input"
            value={editingUrl}
            onChange={(e) => setEditingUrl(e.target.value)}
            placeholder="Paste image URL..."
            className="text-sm"
          />
        </div>

        {/* Live preview */}
        {previewUrl && (
          <div>
            <p className="text-xs text-neutral-500 mb-1">Live preview</p>
            <div className="aspect-video rounded-lg overflow-hidden bg-neutral-100 border">
              {previewError ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-red-400">
                  <AlertTriangle className="w-6 h-6 mb-1" />
                  <span className="text-xs">Failed to load image</span>
                </div>
              ) : (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  onError={() => setPreviewError(true)}
                  onLoad={() => setPreviewError(false)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="border-t p-4 space-y-3">
        <div className="flex gap-2">
          <Button onClick={onSave} disabled={isSaving} className="flex-1">
            <Save className="w-4 h-4 mr-1" />
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button variant="outline" onClick={onClear} disabled={isSaving} className="text-destructive border-destructive/30 hover:bg-destructive/10">
            <Trash2 className="w-4 h-4 mr-1" />
            Clear
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onPrev} disabled={!hasPrev} className="flex-1">
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>
          <Button variant="outline" size="sm" onClick={onNext} disabled={!hasNext} className="flex-1">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// --- Main page ---
export default function AdminVenueImages() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [, setLocation] = useLocation();

  // Read initial state from URL params
  const initParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const [searchTerm, setSearchTerm] = useState(initParams.get("q") ?? "");
  const [selectedState, setSelectedState] = useState(initParams.get("state") ?? "");
  const [imageStatusFilter, setImageStatusFilter] = useState<"" | "has_image" | "no_image">(
    (initParams.get("status") as "" | "has_image" | "no_image") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    Math.max(1, parseInt(initParams.get("page") ?? "1", 10) || 1)
  );
  const [selectedVenueId, setSelectedVenueId] = useState<number | null>(() => {
    const v = initParams.get("selected");
    return v ? parseInt(v, 10) || null : null;
  });
  const [editingUrl, setEditingUrl] = useState("");
  const [brokenImageIds, setBrokenImageIds] = useState<Set<number>>(new Set());
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const searchSyncRef = useRef<ReturnType<typeof setTimeout>>();

  const itemsPerPage = 200;

  // Sync state → URL params (debounced for search term)
  useEffect(() => {
    if (searchSyncRef.current) clearTimeout(searchSyncRef.current);
    searchSyncRef.current = setTimeout(() => {
      const params = new URLSearchParams();
      if (searchTerm) params.set("q", searchTerm);
      if (selectedState && selectedState !== "all") params.set("state", selectedState);
      if (imageStatusFilter) params.set("status", imageStatusFilter);
      if (currentPage > 1) params.set("page", String(currentPage));
      if (selectedVenueId) params.set("selected", String(selectedVenueId));
      const qs = params.toString();
      const newUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
      window.history.replaceState(null, "", newUrl);
    }, 300);
    return () => { if (searchSyncRef.current) clearTimeout(searchSyncRef.current); };
  }, [searchTerm, selectedState, imageStatusFilter, currentPage, selectedVenueId]);

  // Debounce preview URL
  useEffect(() => {
    setPreviewError(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPreviewUrl(editingUrl.trim());
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [editingUrl]);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, selectedState, imageStatusFilter]);

  // Reset broken images on page/filter change
  useEffect(() => { setBrokenImageIds(new Set()); }, [currentPage, searchTerm, selectedState, imageStatusFilter]);

  // --- Queries ---
  const { data: paginated, isLoading } = useQuery<PaginatedResult<Venue>>({
    queryKey: [
      "/api/venues/paginated",
      { q: searchTerm, state: selectedState || undefined, imageStatus: imageStatusFilter || undefined, page: currentPage, limit: itemsPerPage },
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.set("q", searchTerm);
      if (selectedState && selectedState !== "all") params.set("state", selectedState);
      if (imageStatusFilter) params.set("imageStatus", imageStatusFilter);
      params.set("page", String(currentPage));
      params.set("limit", String(itemsPerPage));
      const res = await fetch(`/api/venues/paginated?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load venues");
      return res.json();
    },
  });

  const { data: imageStats } = useQuery<ImageStats>({
    queryKey: ["/api/venues/image-stats"],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const res = await fetch("/api/venues/image-stats", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Failed to load stats");
      return res.json();
    },
  });

  const venues = paginated?.items ?? [];
  const totalItems = paginated?.totalItems ?? 0;
  const totalPages = paginated?.totalPages ?? 0;
  const states = useMemo(
    () => [...new Set(venues.map((v) => v.state).filter((s) => s?.trim()))].sort(),
    [venues]
  );

  // Populate editing URL when selected venue loads from URL param on initial render
  const initialSelectedApplied = useRef(false);
  useEffect(() => {
    if (initialSelectedApplied.current) return;
    if (selectedVenueId && venues.length > 0) {
      const v = venues.find((venue) => venue.id === selectedVenueId);
      if (v) {
        setEditingUrl(v.imageUrl ?? "");
        initialSelectedApplied.current = true;
      }
    }
  }, [selectedVenueId, venues]);

  // Selected venue
  const selectedVenue = useMemo(
    () => venues.find((v) => v.id === selectedVenueId) ?? null,
    [venues, selectedVenueId]
  );
  const selectedIndex = useMemo(
    () => venues.findIndex((v) => v.id === selectedVenueId),
    [venues, selectedVenueId]
  );

  // --- Mutation ---
  const updateMutation = useMutation({
    mutationFn: (data: { id: number; imageUrl: string | null }) =>
      apiRequest(`/api/venues/${data.id}`, { method: "PUT", body: { imageUrl: data.imageUrl } }),
    onSuccess: (updatedVenue: Venue, variables) => {
      queryClient.setQueriesData<PaginatedResult<Venue>>(
        { queryKey: ["/api/venues/paginated"] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((v) => (v.id === variables.id ? { ...v, ...updatedVenue } : v)),
          };
        }
      );
      queryClient.invalidateQueries({ queryKey: ["/api/venues/image-stats"] });
      // Clear broken state for this venue
      setBrokenImageIds((prev) => {
        const next = new Set(prev);
        next.delete(variables.id);
        return next;
      });
      toast({ title: "Image updated", description: `${selectedVenue?.name ?? "Venue"} image saved.` });
    },
    onError: (error: Error) => {
      toast({ title: "Error saving image", description: error.message, variant: "destructive" });
    },
  });

  // --- Handlers ---
  const selectVenue = useCallback(
    (id: number) => {
      setSelectedVenueId(id);
      const v = venues.find((venue) => venue.id === id);
      setEditingUrl(v?.imageUrl ?? "");
      setPreviewUrl("");
      setPreviewError(false);
    },
    [venues]
  );

  const handleSave = useCallback(() => {
    if (!selectedVenueId) return;
    const url = editingUrl.trim() || null;
    updateMutation.mutate({ id: selectedVenueId, imageUrl: url });
  }, [selectedVenueId, editingUrl, updateMutation]);

  const handleClear = useCallback(() => {
    if (!selectedVenueId) return;
    updateMutation.mutate({ id: selectedVenueId, imageUrl: null });
    setEditingUrl("");
  }, [selectedVenueId, updateMutation]);

  const handleClose = useCallback(() => {
    setSelectedVenueId(null);
  }, []);

  const handlePrev = useCallback(() => {
    if (selectedIndex > 0) selectVenue(venues[selectedIndex - 1].id);
  }, [selectedIndex, venues, selectVenue]);

  const handleNext = useCallback(() => {
    if (selectedIndex < venues.length - 1) selectVenue(venues[selectedIndex + 1].id);
  }, [selectedIndex, venues, selectVenue]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!selectedVenueId) return;

      // Don't capture if typing in an input
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") {
        // Only capture Escape and Ctrl/Cmd+S inside inputs
        if (e.key === "Escape") {
          e.preventDefault();
          handleClose();
          return;
        }
        if (e.key === "s" && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          handleSave();
          return;
        }
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        handlePrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleNext();
      } else if (e.key === "s" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedVenueId, handleClose, handlePrev, handleNext, handleSave]);

  // --- Auth guard ---
  if (!user || (user.role !== "admin" && user.role !== "super_admin" && user.role !== "crm_user")) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 pt-24 text-center">
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">Access Denied</h1>
          <p className="text-neutral-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const panelOpen = selectedVenue !== null;

  const sidePanelProps = selectedVenue
    ? {
        venue: selectedVenue,
        editingUrl,
        setEditingUrl,
        previewUrl,
        previewError,
        setPreviewError,
        onSave: handleSave,
        onClear: handleClear,
        onClose: handleClose,
        onPrev: handlePrev,
        onNext: handleNext,
        isSaving: updateMutation.isPending,
        hasPrev: selectedIndex > 0,
        hasNext: selectedIndex < venues.length - 1,
      }
    : null;

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8">
        {/* Header + Stats */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
              <ImageIcon className="w-6 h-6" />
              Venue Image Review
            </h1>
            <p className="text-sm text-neutral-500 mt-1">Scan and fix venue images across all venues</p>
          </div>
          {imageStats && (
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">{imageStats.total.toLocaleString()} total</Badge>
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {imageStats.withImage.toLocaleString()} with image
              </Badge>
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                {imageStats.withoutImage.toLocaleString()} no image
              </Badge>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search venues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="w-40">
            <Select value={selectedState} onValueChange={setSelectedState}>
              <SelectTrigger>
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                {states.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex rounded-lg border overflow-hidden">
            {(
              [
                { value: "" as const, label: "All" },
                { value: "has_image" as const, label: "Has Image" },
                { value: "no_image" as const, label: "No Image" },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setImageStatusFilter(opt.value)}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  imageStatusFilter === opt.value
                    ? "bg-primary text-primary-foreground"
                    : "bg-background hover:bg-neutral-100 text-neutral-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main area: grid + panel */}
        <div className="flex gap-6">
          {/* Thumbnail grid */}
          <div className="flex-1 min-w-0">
            {isLoading ? (
              <div className={`grid gap-2 ${panelOpen && !isMobile ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"}`}>
                {[...Array(24)].map((_, i) => (
                  <div key={i} className="aspect-square rounded-lg bg-neutral-200 animate-pulse" />
                ))}
              </div>
            ) : venues.length === 0 ? (
              <div className="text-center py-16">
                <ImageOff className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
                <p className="text-lg font-medium text-neutral-600">No venues found</p>
                <p className="text-sm text-neutral-400">Try adjusting your search filters</p>
              </div>
            ) : (
              <div
                className={`grid gap-2 ${
                  panelOpen && !isMobile
                    ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
                    : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
                }`}
              >
                {venues.map((v) => (
                  <VenueImageThumbnail
                    key={v.id}
                    venue={v}
                    isSelected={v.id === selectedVenueId}
                    isBroken={brokenImageIds.has(v.id)}
                    onSelect={() => selectVenue(v.id)}
                    onBroken={() => setBrokenImageIds((prev) => new Set(prev).add(v.id))}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            <Pagination
              className="mt-6"
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(p) => {
                setCurrentPage(p);
                setSelectedVenueId(null);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </div>

          {/* Desktop side panel */}
          {panelOpen && !isMobile && sidePanelProps && (
            <div className="w-[400px] shrink-0 border rounded-lg bg-background shadow-lg sticky top-20 self-start max-h-[calc(100vh-6rem)] overflow-hidden flex flex-col">
              <EditSidePanel {...sidePanelProps} />
            </div>
          )}
        </div>

        {/* Mobile Sheet */}
        {isMobile && (
          <Sheet open={panelOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
            <SheetContent side="right" className="w-full sm:max-w-md p-0">
              <SheetHeader className="sr-only">
                <SheetTitle>Edit Venue Image</SheetTitle>
              </SheetHeader>
              {sidePanelProps && <EditSidePanel {...sidePanelProps} />}
            </SheetContent>
          </Sheet>
        )}
      </div>
    </div>
  );
}
